// @ts-check
// =============================================================================
// js/ui/balanced-rows.js — even out a wrapping card grid's rows.
//
// A flex-wrapped grid of N fixed-width cards breaks wherever the viewport
// says: 12 leaders can land as 8+4 or 5+5+2 — lopsided rows that read badly.
// This balances them, built to hold up as the roster grows (14, 20, 100…):
//
//   RULES (in priority order)
//   1. Fewest rows the width allows:      rows = ceil(n / fit)
//   2. Rows as even as possible:          sizes differ by at most 1
//   3. Big rows on top:                   n % rows rows of ceil(n/rows) first,
//                                         then rows of floor(n/rows) — so the
//                                         TOP row is always >= every other row
//                                         and the BOTTOM row is always <=.
//
//     12 / fits 8  -> 6 + 6              13 / fits 5  -> 5 + 4 + 4
//     14 / fits 12 -> 7 + 7              100 / fits 8 -> 8×9 then 7×4
//
// HOW: a width cap alone can't do this (greedy wrap would fill 13 as 5+5+3),
// so the helper GROUPS the cards into real row containers — each row a
// centered flex line — and regroups whenever the math changes. Re-parenting
// keeps the cards' event listeners; callers just run it after every (re)paint
// and on resize. No-op unless the row sizes actually change.
// =============================================================================

/** Marker class for the row containers the helper creates. */
const ROW_CLASS = 'balanced-row';

/**
 * The balanced row sizes for n cards when `fit` fit per line.
 * @param {number} n
 * @param {number} fit
 * @returns {number[]} Non-increasing row sizes summing to n.
 */
export function balancedRowSizes(n, fit) {
	const rows = Math.max(1, Math.ceil(n / Math.max(1, fit)));
	const base = Math.floor(n / rows);
	const extra = n % rows; // the first `extra` rows carry one more card
	return Array.from({ length: rows }, (_, i) => (i < extra ? base + 1 : base));
}

/**
 * Balance a wrapping grid's rows in place.
 * @param {Element|null} grid  The card container. Its cards may be direct
 *   children (first run) or already inside .balanced-row wrappers (reruns).
 * @returns {void}
 */
export function balanceRows(grid) {
	const el = /** @type {HTMLElement|null} */ (grid);
	if (!el || !el.parentElement) return;

	// The cards, in order — looking through any wrappers from a previous run.
	const cards = /** @type {HTMLElement[]} */ ([...el.children].flatMap((child) =>
		child.classList.contains(ROW_CLASS) ? [...child.children] : [child]));
	const n = cards.length;
	if (!n) return;

	const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
	const cardW = cards[0].getBoundingClientRect().width;
	if (!cardW) return; // display:none / not laid out yet — nothing to measure
	// The grid itself is the line width (it has no padding and is never
	// shrunk by this helper, so there's no measurement feedback loop).
	const avail = el.clientWidth;
	const fit = Math.max(1, Math.floor((avail + gap) / (cardW + gap)));
	const sizes = balancedRowSizes(n, fit);

	// Already grouped exactly like this? Don't touch the DOM.
	const current = [...el.children].map((child) =>
		child.classList.contains(ROW_CLASS) ? child.children.length : -1);
	if (current.length === sizes.length && current.every((count, i) => count === sizes[i])) {
		return;
	}

	// Regroup: one centered flex line per row (re-parenting keeps listeners).
	let at = 0;
	const rows = sizes.map((size) => {
		const row = document.createElement('div');
		row.className = ROW_CLASS;
		row.style.display = 'flex';
		row.style.justifyContent = 'center';
		row.style.gap = gap + 'px';
		// Full-width: each row claims its own flex line in the host grid,
		// with the grid's own gap providing the vertical rhythm between rows.
		row.style.width = '100%';
		row.append(...cards.slice(at, at + size));
		at += size;
		return row;
	});
	el.replaceChildren(...rows);
}
